import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MiniSparkline } from "@/components/RankHistoryChart";
import { KW } from "@/constants/testIds";
import { Search, RefreshCw, Plus, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const intentTone = {
  commercial: "bg-[hsl(var(--chart-1))]/15 text-[hsl(var(--chart-1))]",
  transactional: "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))]",
  informational: "bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))]",
  navigational: "bg-[hsl(var(--chart-5))]/15 text-[hsl(var(--chart-5))]",
  local: "bg-[hsl(var(--chart-4))]/15 text-[hsl(var(--chart-4))]",
};

function positionTone(p) {
  if (p <= 3) return "text-[hsl(var(--chart-3))]";
  if (p <= 10) return "text-[hsl(var(--chart-1))]";
  if (p <= 30) return "text-[hsl(var(--chart-2))]";
  return "text-muted-foreground";
}

export default function Keywords() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [taskId, setTaskId] = useState(null);
  const [task, setTask] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [activeCluster, setActiveCluster] = useState(null);
  const pollRef = useRef(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["keywords", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/keywords`)).data,
    enabled: !!selectedShopId,
  });

  const { data: clusterData } = useQuery({
    queryKey: ["clusters", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/clusters`)).data,
    enabled: !!selectedShopId,
  });
  const clusters = clusterData?.clusters || [];

  useEffect(() => {
    setTaskId(null);
    setTask(null);
    if (pollRef.current) clearInterval(pollRef.current);
  }, [selectedShopId]);

  const refreshMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/keywords/refresh/async`)).data,
    onSuccess: (data) => {
      setTaskId(data.task_id);
      setTask({ status: "pending", completed: 0, total: rows.length, live_count: 0 });
    },
    onError: () => toast.error("Refresh failed to start"),
  });

  const addMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post(`/shops/${selectedShopId}/keywords`, payload)).data,
    onSuccess: (data) => {
      const src = data.source === "dataforseo" ? "DataForSEO" : "Mock";
      toast.success(`Keyword hinzugefügt · Position ${data.keyword.position} via ${src}`);
      setAddOpen(false);
      qc.invalidateQueries({ queryKey: ["keywords", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["clusters", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["overview", selectedShopId] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Fehler beim Hinzufügen"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, cluster }) =>
      (await api.patch(`/shops/${selectedShopId}/keywords/${id}`, { cluster })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["keywords", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["clusters", selectedShopId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (kwId) =>
      (await api.delete(`/shops/${selectedShopId}/keywords/${kwId}`)).data,
    onSuccess: () => {
      toast.success("Keyword entfernt");
      qc.invalidateQueries({ queryKey: ["keywords", selectedShopId] });
      qc.invalidateQueries({ queryKey: ["overview", selectedShopId] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen"),
  });

  useEffect(() => {
    if (!taskId) return;
    const poll = async () => {
      try {
        const res = await api.get(`/refresh/tasks/${taskId}`);
        setTask(res.data);
        if (res.data.status === "done") {
          toast.success(`Refreshed · ${res.data.live_count}/${res.data.total} live from DataForSEO`);
          clearInterval(pollRef.current);
          setTaskId(null);
          qc.invalidateQueries({ queryKey: ["keywords", selectedShopId] });
          qc.invalidateQueries({ queryKey: ["overview", selectedShopId] });
        } else if (res.data.status === "failed") {
          toast.error(res.data.error || "Refresh failed");
          clearInterval(pollRef.current);
          setTaskId(null);
        }
      } catch {
        /* keep polling */
      }
    };
    pollRef.current = setInterval(poll, 2000);
    poll();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [taskId, selectedShopId, qc]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (activeCluster && (r.cluster || null) !== activeCluster) return false;
        return r.keyword.toLowerCase().includes(q.toLowerCase());
      }),
    [rows, q, activeCluster],
  );

  const isRefreshing = task?.status === "pending" || task?.status === "running";
  const progressPct = task?.total ? Math.round((task.completed / task.total) * 100) : 0;

  return (
    <div data-testid={KW.root} className="space-y-6 rk-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {selectedShop?.domain}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {t("nav_keywords")}
          </h1>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid={KW.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_keywords")}
              className="pl-9 h-10 font-mono text-sm"
            />
          </div>
          <Button
            data-testid={KW.refreshBtn}
            onClick={() => refreshMutation.mutate()}
            disabled={isRefreshing || !rows.length}
            variant="outline"
            className="h-10 gap-2"
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            {isRefreshing ? t("refreshing") : t("refresh_ranks")}
          </Button>
          <Button
            data-testid="add-keyword-btn"
            onClick={() => setAddOpen(true)}
            disabled={!selectedShopId}
            className="h-10 gap-2"
          >
            <Plus className="h-4 w-4" />
            Keyword
          </Button>
        </div>
      </header>

      {isRefreshing && (
        <div className="border border-border bg-card rounded-lg px-5 py-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground mb-2">
              <span>
                DataForSEO live pull ·{" "}
                <span className="text-foreground">{task?.completed ?? 0}</span> /{" "}
                {task?.total ?? "—"}
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-action transition-[width] duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {clusters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="cluster-filter-bar">
          <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mr-1">
            Cluster
          </span>
          <button
            type="button"
            onClick={() => setActiveCluster(null)}
            data-testid="cluster-chip-all"
            className={cn(
              "px-3 py-1 rounded-full text-xs font-mono border transition-colors",
              !activeCluster
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:border-foreground/40",
            )}
          >
            Alle · {rows.length}
          </button>
          {clusters.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setActiveCluster(c.name === activeCluster ? null : c.name)}
              data-testid={`cluster-chip-${c.name}`}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-mono border transition-colors",
                activeCluster === c.name
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:border-foreground/40",
              )}
            >
              {c.name} · {c.count}
            </button>
          ))}
        </div>
      )}

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid={KW.table} className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <Th className="text-left">{t("keyword")}</Th>
                <Th className="text-left">Cluster</Th>
                <Th className="text-left">{t("intent")}</Th>
                <Th className="text-right">{t("position")}</Th>
                <Th className="text-right">{t("change")}</Th>
                <Th className="text-right">{t("search_volume")}</Th>
                <Th className="text-right">{t("difficulty")}</Th>
                <Th className="text-right">{t("cpc")}</Th>
                <Th className="text-left hidden xl:table-cell">{t("url")}</Th>
                <Th className="text-right">{t("trend_30d")}</Th>
                <Th className="text-right"></Th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={11} className="p-6 text-center text-muted-foreground text-sm">
                    …
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-6 text-center text-muted-foreground text-sm">
                    —
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const change = r.previous_position - r.position;
                return (
                  <tr
                    key={r.keyword_id}
                    data-testid={KW.row(r.keyword_id)}
                    className="border-b border-border/70 last:border-0 hover:bg-secondary/40 transition-colors"
                  >
                    <Td className="text-left font-medium max-w-[280px] truncate">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{r.keyword}</span>
                        {r.source !== "dataforseo" && (
                          <span
                            className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 text-amber-500 border border-amber-500/30"
                            title="Diese Position ist NICHT von Google verifiziert! Klick 'Rankings aktualisieren' um echte Zahlen zu holen."
                            data-testid="kw-mock-badge"
                          >
                            MOCK
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="text-left">
                      <ClusterCell
                        value={r.cluster}
                        clusters={clusters}
                        onSave={(newCluster) =>
                          updateMutation.mutate({ id: r.keyword_id, cluster: newCluster })
                        }
                      />
                    </Td>
                    <Td className="text-left">
                      <Badge
                        variant="outline"
                        className={cn(
                          "font-mono text-[10px] uppercase tracking-wider border-transparent",
                          intentTone[r.intent] ?? "bg-secondary text-muted-foreground",
                        )}
                      >
                        {t(r.intent)}
                      </Badge>
                    </Td>
                    <Td className={cn("text-right font-mono tabular", positionTone(r.position))}>
                      {r.position}
                    </Td>
                    <Td className="text-right font-mono tabular">
                      <span
                        className={cn(
                          change > 0
                            ? "text-[hsl(var(--chart-3))]"
                            : change < 0
                              ? "text-destructive"
                              : "text-muted-foreground",
                        )}
                      >
                        {change > 0 ? `▲ ${change}` : change < 0 ? `▼ ${Math.abs(change)}` : "•"}
                      </span>
                    </Td>
                    <Td className="text-right font-mono tabular text-muted-foreground">
                      {new Intl.NumberFormat("en-US").format(r.search_volume)}
                    </Td>
                    <Td className="text-right font-mono tabular">
                      <DifficultyBadge value={r.difficulty} />
                    </Td>
                    <Td className="text-right font-mono tabular text-muted-foreground">
                      €{r.cpc.toFixed(2)}
                    </Td>
                    <Td className="text-left font-mono text-xs text-muted-foreground hidden xl:table-cell max-w-[220px] truncate">
                      {r.url}
                    </Td>
                    <Td className="text-right">
                      <div className="flex justify-end">
                        <MiniSparkline data={r.history} />
                      </div>
                    </Td>
                    <Td className="text-right">
                      <button
                        onClick={() => deleteMutation.mutate(r.keyword_id)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                        title="Keyword entfernen"
                        data-testid={`delete-keyword-${r.keyword_id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AddKeywordDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(payload) => addMutation.mutate(payload)}
        loading={addMutation.isPending}
        clusters={clusters}
        defaultCluster={activeCluster || ""}
      />
    </div>
  );
}

function Th({ children, className }) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-mono font-semibold text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}
function Td({ children, className }) {
  return <td className={cn("px-4 h-12 align-middle", className)}>{children}</td>;
}

function DifficultyBadge({ value }) {
  const color =
    value <= 30
      ? "hsl(var(--chart-3))"
      : value <= 60
        ? "hsl(var(--chart-2))"
        : "hsl(var(--destructive))";
  return (
    <span className="inline-flex items-center gap-1.5 tabular">
      <span className="w-8 h-1.5 rounded-full bg-secondary overflow-hidden inline-block">
        <span
          className="block h-full"
          style={{ width: `${value}%`, background: color }}
        />
      </span>
      <span>{value}</span>
    </span>
  );
}

function AddKeywordDialog({ open, onOpenChange, onSubmit, loading, clusters = [], defaultCluster = "" }) {
  const [keyword, setKeyword] = useState("");
  const [url, setUrl] = useState("");
  const [intent, setIntent] = useState("transactional");
  const [cluster, setCluster] = useState("");

  useEffect(() => {
    if (!open) {
      setKeyword("");
      setUrl("");
      setIntent("transactional");
      setCluster("");
    } else {
      setCluster(defaultCluster || "");
    }
  }, [open, defaultCluster]);

  const submit = () => {
    if (!keyword.trim()) return;
    onSubmit({
      keyword: keyword.trim(),
      url: url.trim() || undefined,
      intent,
      cluster: cluster.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-keyword-dialog">
        <DialogHeader>
          <DialogTitle>Neues Keyword tracken</DialogTitle>
          <DialogDescription>
            Rankly holt beim Hinzufügen sofort die aktuelle Google-Position via DataForSEO
            (falls konfiguriert) und trackt sie ab jetzt täglich.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              Keyword *
            </label>
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="z.B. flughafentransfer zürich"
              className="mt-1 font-mono"
              data-testid="add-keyword-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && keyword.trim()) submit();
              }}
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              URL (optional)
            </label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="z.B. /flughafentransfer/zuerich"
              className="mt-1 font-mono text-sm"
              data-testid="add-keyword-url-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Die Seite auf der du für dieses Keyword ranken willst. Leer lassen für Homepage.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              Cluster (optional)
            </label>
            <Input
              value={cluster}
              onChange={(e) => setCluster(e.target.value)}
              placeholder="z.B. Hurghada, Marsa Alam, Tauchen …"
              className="mt-1 font-mono text-sm"
              data-testid="add-keyword-cluster-input"
              list="cluster-suggestions"
            />
            <datalist id="cluster-suggestions">
              {clusters.map((c) => (
                <option key={c.name} value={c.name}>{`${c.count} Keywords`}</option>
              ))}
            </datalist>
            {clusters.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="add-keyword-cluster-suggestions">
                {clusters.slice(0, 8).map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setCluster(c.name)}
                    className={cn(
                      "px-2 py-0.5 rounded-full text-[11px] font-mono border transition-colors",
                      cluster === c.name
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-muted-foreground border-border hover:border-foreground/40",
                    )}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Themen-Bündelung für Topical Authority. Frei tippen oder aus Vorschlägen wählen.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              Intent
            </label>
            <Select value={intent} onValueChange={setIntent}>
              <SelectTrigger className="mt-1" data-testid="add-keyword-intent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="transactional">Transaktional (kauf-orientiert)</SelectItem>
                <SelectItem value="commercial">Kommerziell (recherchierend)</SelectItem>
                <SelectItem value="informational">Informational (Fragen)</SelectItem>
                <SelectItem value="navigational">Navigational (Marke)</SelectItem>
                <SelectItem value="local">Local (Ort-bezogen)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={!keyword.trim() || loading}
            data-testid="add-keyword-submit"
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Hinzufügen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClusterCell({ value, clusters, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if ((draft || "") !== (value || "")) onSave(draft.trim() || "");
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if ((draft || "") !== (value || "")) onSave(draft.trim() || "");
              setEditing(false);
            } else if (e.key === "Escape") {
              setDraft(value || "");
              setEditing(false);
            }
          }}
          placeholder="Cluster…"
          list="cluster-suggestions"
          className="h-7 text-xs font-mono w-32"
          data-testid="cluster-cell-input"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        "px-2 py-0.5 rounded-full text-[11px] font-mono border transition-colors",
        value
          ? "bg-secondary text-foreground border-border hover:border-foreground/40"
          : "text-muted-foreground/50 border-dashed border-border hover:text-foreground hover:border-foreground/40",
      )}
      data-testid="cluster-cell-btn"
      title="Cluster bearbeiten"
    >
      {value || "+ Cluster"}
    </button>
  );
}
